/**
 * Airport Flyover Video Mapping
 * 
 * Videos are automatically discovered from /public/videos/airports/
 * Just drop an MP4 file named with the IATA code (e.g., LHR.mp4, JFK.mp4)
 * and it will automatically be available.
 * 
 * To add a new airport video:
 * 1. Create flyover in Google Earth Studio
 * 2. Export as MP4 (1080p recommended)
 * 3. Save as {IATA}.mp4 (e.g., LHR.mp4)
 * 4. Copy to /public/videos/airports/
 * 5. Done! No code changes needed.
 */

export interface AirportVideo {
  url: string;
  thumbnail?: string; // optional poster image
}

// Video storage base URL - update this to your CDN/S3 bucket
const VIDEO_BASE_URL = import.meta.env.VITE_AIRPORT_VIDEOS_URL || '/videos/airports';

/**
 * Airport videos available in the system
 * Add IATA codes here as you create videos
 */
const AVAILABLE_VIDEOS = [
  'AMS', 'BRS', 'CDG', 'DXB', 'JFK', 'LAS', 'LGW', 'LHR', 'MAN', 'MCO'
];

/**
 * Automatically generate video mappings based on IATA codes
 */
const airportVideos: Record<string, AirportVideo> = {};
AVAILABLE_VIDEOS.forEach(iata => {
  airportVideos[iata] = {
    url: `${VIDEO_BASE_URL}/${iata}.mp4`
  };
});

export { airportVideos };

/**
 * Check if an airport has a flyover video
 */
export function hasAirportVideo(iata: string): boolean {
  return iata.toUpperCase() in airportVideos;
}

/**
 * Get video details for an airport
 */
export function getAirportVideo(iata: string): AirportVideo | null {
  return airportVideos[iata.toUpperCase()] || null;
}

/**
 * Get all airports with videos (useful for UI indicators)
 */
export function getAirportsWithVideos(): string[] {
  return Object.keys(airportVideos);
}
